/**
 * Count-up for the stat band.
 *
 * The final figure is already printed in the HTML by the section. This script
 * only replaces it with a lower number and walks it back up when the band
 * scrolls into view. Everything here is therefore optional decoration:
 *
 *   - script blocked        -> the real number is on screen, unmoved
 *   - IntersectionObserver missing -> counts immediately, still ends correct
 *   - reduced motion        -> left alone entirely
 *   - value is not a plain number ("3-7", "TX") -> skipped, never mangled
 *
 * The number is never overwritten until requestAnimationFrame has actually
 * fired, and a watchdog restores the true value if the animation stalls
 * part-way. Both matter: rAF is suspended in background tabs, and an earlier
 * revision that zeroed the value up front was caught on screen showing 0 and 1
 * where the real figures were 5 and 6.
 */
(function () {
  'use strict';

  var DURATION = 1100;

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* Only plain integers animate. Anything with a dash, letter or slash in it is
     a range or a label and gets left exactly as authored. */
  function parseTarget(raw) {
    if (!raw) return null;
    var trimmed = String(raw).trim();
    if (!/^\d{1,9}$/.test(trimmed)) return null;
    return parseInt(trimmed, 10);
  }

  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function run(el, target) {
    var numberEl = el.querySelector('.stat-band__number');
    if (!numberEl) return;

    var started = null;
    var done = false;

    function finish() {
      if (done) return;
      done = true;
      // Always land on the authored value, never on rounding output.
      numberEl.textContent = String(target);
    }

    function frame(now) {
      if (done) return;
      if (started === null) started = now;
      var t = Math.min((now - started) / DURATION, 1);
      if (t >= 1) {
        finish();
        return;
      }
      numberEl.textContent = String(Math.round(easeOut(t) * target));
      window.requestAnimationFrame(frame);
    }

    /* Deliberately NOT zeroed here.
     *
     * requestAnimationFrame is suspended in background tabs. Zeroing up front
     * and then waiting for a callback that never arrives leaves a visibly wrong
     * figure on screen — observed directly: a band left showing 0 and 1 where
     * the real values were 5 and 6. Writing the first value from inside the
     * callback means the authored number stays put until rAF has proved it is
     * actually running. */
    window.requestAnimationFrame(frame);

    /* Watchdog for the other half of the same problem: rAF that starts, gets
     * suspended part-way, and strands the number mid-count. Well clear of the
     * animation's own duration, so it only ever fires on a stall. */
    setTimeout(finish, DURATION + 2000);
  }

  function init() {
    var nodes = document.querySelectorAll('[data-count-up]');
    if (!nodes.length) return;

    if (prefersReducedMotion() || !window.requestAnimationFrame) return;

    var pending = [];
    for (var i = 0; i < nodes.length; i++) {
      var target = parseTarget(nodes[i].getAttribute('data-count-up'));
      if (target === null || target === 0) continue;
      pending.push({ el: nodes[i], target: target });
    }
    if (!pending.length) return;

    if (!('IntersectionObserver' in window)) {
      for (var j = 0; j < pending.length; j++) run(pending[j].el, pending[j].target);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (!entries[k].isIntersecting) continue;
          var entry = entries[k];
          observer.unobserve(entry.target);
          for (var m = 0; m < pending.length; m++) {
            if (pending[m].el === entry.target) {
              run(pending[m].el, pending[m].target);
              break;
            }
          }
        }
      },
      { threshold: 0.4 }
    );

    for (var n = 0; n < pending.length; n++) observer.observe(pending[n].el);
  }

  /* Fail-open wrapper. Every figure is already correct in the HTML before this
     file runs, so the safest possible outcome of any error is to do nothing at
     all and leave the markup alone. */
  function safeInit() {
    try {
      init();
    } catch (e) {
      /* Intentionally silent: the authored numbers are still on screen. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
})();
